/**
 * Folder-tree partitioning for a "Procedure run" evidence bundle (E10).
 *
 * A runbook execution bundle's per-step EvidenceFiles carry
 * `metadata.folder` = `step-NN-<slug>` and `metadata.stepOrder`. Root files
 * (manifest.json / README.md) carry no folder. This splits
 * a flat file list into root files + per-step folders sorted by execution order,
 * so `ArtifactsSection` can render a browsable tree. Old bundles (no folder on
 * any file) yield an empty `folders` list → callers fall back to a flat render.
 */
export type BundleTreeFile = {
  metadata?: Record<string, unknown> | null
}

export function folderOfBundleFile(file: BundleTreeFile): string | null {
  const v = file.metadata?.folder
  return typeof v === 'string' && v ? v : null
}

export function partitionRunbookBundleFiles<T extends BundleTreeFile>(
  files: T[],
): { rootFiles: T[]; folders: Array<{ folder: string; files: T[] }> } {
  const rootFiles: T[] = []
  const byFolder = new Map<string, T[]>()
  for (const f of files) {
    const folder = folderOfBundleFile(f)
    if (!folder) {
      rootFiles.push(f)
      continue
    }
    const list = byFolder.get(folder) ?? []
    list.push(f)
    byFolder.set(folder, list)
  }

  const orderOf = (folder: string): number => {
    for (const f of byFolder.get(folder) ?? []) {
      const o = f.metadata?.stepOrder
      if (typeof o === 'number') return o
    }
    return Number.MAX_SAFE_INTEGER
  }

  const folders = [...byFolder.keys()]
    .sort((a, b) => orderOf(a) - orderOf(b))
    .map((folder) => ({ folder, files: byFolder.get(folder) ?? [] }))

  return { rootFiles, folders }
}
